// Cart Drawer Enhancements for Better Conversions
//
// Features:
// - Conditional logging: Change this.DEBUG = false to this.DEBUG = true for development
// - Double event binding protection: Prevents setupEventListeners() from running multiple times
// - Singleton pattern: Prevents multiple class instances from being created
// - Efficient URL change detection: Uses popstate + monkey-patched history methods instead of MutationObserver
// - Capped localStorage history: Limits shownProductIds to prevent unbounded growth
// - iOS 100vh fix: Uses --vh custom property to handle Safari address bar collapse
class CartDrawerEnhancements {
  // Global instance to prevent multiple instantiations
  static instance = null;
  constructor() {
    // Prevent multiple instances
    if (CartDrawerEnhancements.instance) {
      console.warn(
        "CartDrawerEnhancements already instantiated, returning existing instance"
      );
      return CartDrawerEnhancements.instance;
    }

    // Debug flag - set to true for development, false for production
    this.DEBUG = false;

    this.crossSellLoaded = false;
    this.lastCartCount = 0;
    this.isLoadingCrossSell = false;
    this.currentRecommendations = []; // Track current recommendations
    this.shownProductIds = new Set(); // Track products we've already shown
    this.previousActiveElement = null; // Store the element that had focus before drawer opened
    this.focusTrapElements = []; // Store focusable elements in the drawer
    this._eventsBound = false; // Flag to prevent double event binding
    this.urlChangeTimeout = null; // Timeout for URL change debouncing
    this.MAX_SHOWN_PRODUCT_IDS = 50; // Cap localStorage history to prevent unbounded growth
    this.vhUpdateTimeout = null; // Timeout for viewport height updates

    // Cart data caching system
    this.cartCache = null; // Cached cart data
    this.cartCacheTimestamp = null; // When the cache was last updated
    this.cartCacheValid = false; // Whether the cache is valid
    this.cartRequest = null; // In-flight fetch promise to prevent race conditions

    // Cache instrumentation for debugging
    this.cacheHitCount = 0;
    this.cacheMissCount = 0;
    this.networkRequestCount = 0;
    this.manualRefreshCount = 0; // Separate counter for manual refreshes

    // Store instance reference
    CartDrawerEnhancements.instance = this;

    this.init();
  }

  // Helper method for conditional logging
  log(...args) {
    if (this.DEBUG) {
      console.log(...args);
    }
  }

  // Cart cache management methods
  isCartCacheValid() {
    if (!this.cartCache || !this.cartCacheTimestamp || !this.cartCacheValid) {
      // Sync the flag when cache is invalid
      this.cartCacheValid = false;
      return false;
    }

    // Cache is valid if the flag is true
    // The flag gets set to false only on explicit invalidation
    return this.cartCacheValid;
  }

  setCartCache(cartData) {
    this.cartCache = cartData;
    this.cartCacheTimestamp = Date.now();
    this.cartCacheValid = true;
    this.log("Cart cache updated:", cartData);
  }

  getCartCache() {
    if (this.isCartCacheValid()) {
      this.log("Using cached cart data");
      return this.cartCache;
    }
    this.log("Cart cache invalid");
    return null;
  }

  invalidateCartCache() {
    this.cartCache = null;
    this.cartCacheTimestamp = null;
    this.cartCacheValid = false;
    this.log("Cart cache invalidated");
  }

  async fetchCartData(forceRefresh = false) {
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh) {
      const cachedData = this.getCartCache();
      if (cachedData) {
        this.cacheHitCount++;
        this.log(
          "Cache HIT - using cached cart data (hits:",
          this.cacheHitCount,
          "misses:",
          this.cacheMissCount,
          ")"
        );
        return cachedData;
      }
    }

    // Capture existing request first to prevent race conditions
    const existingRequest = this.cartRequest;
    if (existingRequest) {
      this.log("Returning existing in-flight cart request");
      return existingRequest;
    }

    // Set flags before force refresh to prevent stale cache
    if (forceRefresh) {
      this.cartCacheValid = false;
      this.cartCacheTimestamp = null; // Clear timestamp for invalid cache
      this.manualRefreshCount++; // Track manual refreshes separately
    }

    // Fetch fresh data from server
    // Only increment cache miss count for actual cache misses, not manual refreshes
    if (!forceRefresh) {
      this.cacheMissCount++;
    }
    this.networkRequestCount++;

    const requestType = forceRefresh ? "MANUAL REFRESH" : "Cache MISS";
    this.log(
      requestType + " - fetching fresh cart data from server (hits:",
      this.cacheHitCount,
      "misses:",
      this.cacheMissCount,
      "network:",
      this.networkRequestCount,
      "manual:",
      this.manualRefreshCount,
      ")"
    );

    try {
      // Create and assign the fetch promise BEFORE any await/then operations
      this.cartRequest = fetch("/cart.js").then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      });

      const cartData = await this.cartRequest;

      // Cache the fresh data
      this.setCartCache(cartData);

      return cartData;
    } catch (error) {
      console.error("Error fetching cart data:", error);
      // Clear the failed promise to allow retries
      this.cartRequest = null;
      throw error;
    } finally {
      // Always clear the request promise when done
      this.cartRequest = null;
    }
  }

  // Cache debugging and monitoring methods
  getCacheStats() {
    const now = Date.now();
    const cacheAge = this.cartCacheTimestamp
      ? now - this.cartCacheTimestamp
      : null;

    // Calculate accurate hit rate (excluding manual refreshes)
    const totalCacheRequests = this.cacheHitCount + this.cacheMissCount;
    const hitRate =
      totalCacheRequests > 0
        ? Math.round((this.cacheHitCount / totalCacheRequests) * 100) + "%"
        : "N/A";

    // Calculate overall efficiency (all requests vs cache hits)
    const totalRequests =
      this.cacheHitCount + this.cacheMissCount + this.manualRefreshCount;
    const efficiencyRate =
      totalRequests > 0
        ? Math.round((this.cacheHitCount / totalRequests) * 100) + "%"
        : "N/A";

    return {
      hasCache: !!this.cartCache,
      isValid: this.cartCacheValid,
      cacheAge: cacheAge ? Math.round(cacheAge / 1000) + "s" : "N/A",
      cacheDuration: "Infinite (until busted)",
      itemCount: this.cartCache?.item_count || 0,
      totalPrice: this.cartCache?.total_price || 0,
      // Instrumentation counters
      cacheHits: this.cacheHitCount,
      cacheMisses: this.cacheMissCount,
      manualRefreshes: this.manualRefreshCount,
      networkRequests: this.networkRequestCount,
      // Rate calculations
      cacheHitRate: hitRate, // Cache hits / (cache hits + cache misses)
      overallEfficiency: efficiencyRate, // Cache hits / all requests
      hasInFlightRequest: !!this.cartRequest,
    };
  }

  forceRefreshCartData() {
    this.log("Force refreshing cart data");
    return this.fetchCartData(true);
  }

  async init() {
    this.setupEventListeners();

    // Load previously shown product IDs from localStorage
    this.loadShownProductIds();

    // Setup iOS 100vh fix
    this.setupViewportHeightFix();

    // Initialize lastCartCount with current cart state using cache
    // Reuse the cart object to avoid second fetch
    try {
      const cart = await this.updateCartCount();
      if (cart) {
        this.lastCartCount = cart.item_count;
      } else {
        this.lastCartCount = 0;
      }
    } catch (error) {
      console.error("Error fetching initial cart count:", error);
      this.lastCartCount = 0;
    }

    // Warm the cache on init regardless of server count
    // This ensures cache is primed for later components
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", async () => {
        // Skip warm-up if cache is already valid (updateCartCount already fetched)
        if (!this.cartCacheValid) {
          try {
            await this.fetchCartData();
            this.log("Cache warmed on DOM ready");
          } catch (error) {
            console.error("Error warming cache:", error);
          }
        } else {
          this.log("Cache already valid, skipping DOM ready warm-up");
        }

        setTimeout(() => {
          this.loadCrossSellProducts();
        }, 100);
      });
    } else {
      // DOM is already ready - prime cache immediately if needed
      if (!this.cartCacheValid) {
        try {
          await this.fetchCartData();
          this.log("Cache warmed on init (DOM already ready)");
        } catch (error) {
          console.error("Error warming cache:", error);
        }
      } else {
        this.log("Cache already valid, skipping immediate warm-up");
      }

      setTimeout(() => {
        this.loadCrossSellProducts();
      }, 100);
    }

    // Also load on window load to ensure it works on all pages
    window.addEventListener("load", async () => {
      // Check cache validity immediately for fail-fast error detection
      if (!this.cartCacheValid) {
        try {
          await this.refreshCartData();
        } catch (error) {
          console.error("Error refreshing cart data on window load:", error);
        }
      }

      // Load cross-sell products after a short delay for animations
      setTimeout(async () => {
        this.loadCrossSellProducts();
      }, 200);
    });
  }

  setupEventListeners() {
    // Prevent double event binding
    if (this._eventsBound) {
      this.log("Event listeners already bound, skipping setup");
      return;
    }

    this.log("Setting up event listeners");

    // Listen for cart updates from AJAX add to cart
    document.addEventListener("ajaxProduct:added", async (event) => {
      try {
        // Invalidate cache since cart has changed
        this.invalidateCartCache();
        await this.handleCartUpdate(event.detail);
      } catch (error) {
        console.error("Error handling ajaxProduct:added event:", error);
      }
    });

    // Listen for cart updates from quantity changes and removals
    document.addEventListener("cart:updated", async (event) => {
      try {
        // Invalidate cache since cart has changed
        this.invalidateCartCache();
        await this.handleCartUpdate(event.detail.cart);
      } catch (error) {
        console.error("Error handling cart:updated event:", error);
      }
    });

    // Listen for niche cart invalidators to prevent cache drift
    document.addEventListener("cart:note:updated", () => {
      this.log("Cart note updated, invalidating cache");
      this.invalidateCartCache();
    });

    document.addEventListener("cart:discount:applied", () => {
      this.log("Cart discount applied, invalidating cache");
      this.invalidateCartCache();
    });

    document.addEventListener("cart:cleared", () => {
      this.log("Cart cleared, invalidating cache");
      this.invalidateCartCache();
    });

    // Listen for cart drawer open - multiple event types
    document.addEventListener("drawer:open", () => {
      this.onDrawerOpen();
    });

    // Listen for cart drawer close
    document.addEventListener("drawer:close", () => {
      this.onDrawerClose();
    });

    // Also listen for cart drawer visibility changes
    document.addEventListener("DOMContentLoaded", () => {
      this.setupDrawerObserver();
    });

    // Listen for URL changes (for SPA-like behavior) - more efficient than MutationObserver
    this.setupUrlChangeListener();

    // Update cart count on page load
    document.addEventListener("DOMContentLoaded", async () => {
      await this.updateCartCount();
    });

    // Additional check for cart drawer opening via class changes
    this.setupDrawerClassObserver();

    // Add refresh button event listener
    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-refresh-recommendations]")) {
        event.preventDefault();
        this.refreshRecommendations();
      }
    });

    // Ensure scrolling is unlocked if user navigates away
    window.addEventListener("beforeunload", () => {
      this.unlockBodyScroll();
    });

    // Mark events as bound to prevent double binding
    this._eventsBound = true;
    this.log("Event listeners setup complete");
  }

  // Method to reset event binding flag (useful for testing or re-initialization)
  resetEventBinding() {
    this._eventsBound = false;
    this.log("Event binding flag reset");
  }

  // Static method to clear global instance (useful for testing)
  static clearInstance() {
    CartDrawerEnhancements.instance = null;
  }

  // Static method to check if instance exists
  static getInstance() {
    return CartDrawerEnhancements.instance;
  }

  setupUrlChangeListener() {
    // Efficient URL change detection using popstate + monkey-patched history methods
    // This is much more performant than observing all DOM mutations
    // Store current URL to detect changes
    this.currentUrl = window.location.href;

    // Bind the handleUrlChange method to preserve 'this' context
    this.boundHandleUrlChange = this.handleUrlChange.bind(this);

    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", this.boundHandleUrlChange);

    // Monkey-patch pushState and replaceState to catch programmatic navigation
    this.patchHistoryMethods();

    this.log("URL change listener setup complete");
  }

  patchHistoryMethods() {
    // Store original methods for potential restoration
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;

    // Patch pushState
    history.pushState = (...args) => {
      this.originalPushState.apply(history, args);
      this.handleUrlChange();
    };

    // Patch replaceState
    history.replaceState = (...args) => {
      this.originalReplaceState.apply(history, args);
      this.handleUrlChange();
    };

    this.log("History methods patched for URL change detection");
  }

  handleUrlChange() {
    const newUrl = window.location.href;
    if (newUrl !== this.currentUrl) {
      this.log("URL changed from", this.currentUrl, "to", newUrl);
      this.currentUrl = newUrl;

      // Debounce the cross-sell loading to avoid excessive calls
      clearTimeout(this.urlChangeTimeout);
      this.urlChangeTimeout = setTimeout(() => {
        this.loadCrossSellProducts();
      }, 500);
    }
  }

  // Cleanup method to restore original history methods
  cleanupUrlChangeListener() {
    // Clear any pending timeout
    if (this.urlChangeTimeout) {
      clearTimeout(this.urlChangeTimeout);
      this.urlChangeTimeout = null;
    }

    // Remove popstate listener
    if (this.boundHandleUrlChange) {
      window.removeEventListener("popstate", this.boundHandleUrlChange);
      this.boundHandleUrlChange = null;
    }

    // Restore original history methods if they were stored
    if (this.originalPushState && this.originalReplaceState) {
      history.pushState = this.originalPushState;
      history.replaceState = this.originalReplaceState;
      this.originalPushState = null;
      this.originalReplaceState = null;
    }

    this.log("URL change listener cleanup complete");
  }

  // Check if URL change listener is active
  isUrlChangeListenerActive() {
    return !!(
      this.boundHandleUrlChange &&
      this.originalPushState &&
      this.originalReplaceState
    );
  }

  // iOS 100vh fix - handles Safari address bar collapse
  setupViewportHeightFix() {
    // Set initial viewport height
    this.updateViewportHeight();

    // Listen for resize events (including address bar show/hide)
    window.addEventListener("resize", () => {
      this.debouncedUpdateViewportHeight();
    });

    // Listen for orientation changes
    window.addEventListener("orientationchange", () => {
      // Use a longer delay for orientation changes
      setTimeout(() => {
        this.updateViewportHeight();
      }, 500);
    });

    // Listen for scroll events (address bar can show/hide on scroll)
    window.addEventListener("scroll", () => {
      this.debouncedUpdateViewportHeight();
    });

    this.log("Viewport height fix setup complete");
  }

  updateViewportHeight() {
    // Calculate the actual viewport height and set CSS custom property
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);

    this.log(
      "Updated viewport height:",
      vh,
      "px (window.innerHeight:",
      window.innerHeight,
      ")"
    );
  }

  debouncedUpdateViewportHeight() {
    // Clear existing timeout
    if (this.vhUpdateTimeout) {
      clearTimeout(this.vhUpdateTimeout);
    }

    // Debounce the update to avoid excessive calls
    this.vhUpdateTimeout = setTimeout(() => {
      this.updateViewportHeight();
    }, 100);
  }

  // Cleanup viewport height fix
  cleanupViewportHeightFix() {
    if (this.vhUpdateTimeout) {
      clearTimeout(this.vhUpdateTimeout);
      this.vhUpdateTimeout = null;
    }

    // Remove event listeners
    window.removeEventListener("resize", this.debouncedUpdateViewportHeight);
    window.removeEventListener("orientationchange", this.updateViewportHeight);
    window.removeEventListener("scroll", this.debouncedUpdateViewportHeight);

    this.log("Viewport height fix cleanup complete");
  }

  // Check if viewport height fix is active
  isViewportHeightFixActive() {
    return !!(this.vhUpdateTimeout !== undefined);
  }

  // Get current viewport height value
  getCurrentViewportHeight() {
    const vh = getComputedStyle(document.documentElement).getPropertyValue(
      "--vh"
    );
    return {
      vh: vh,
      windowInnerHeight: window.innerHeight,
      calculatedVh: window.innerHeight * 0.01,
    };
  }

  // Manually trigger viewport height update (useful for testing)
  forceViewportHeightUpdate() {
    this.updateViewportHeight();
    this.log("Forced viewport height update");
  }

  setupDrawerObserver() {
    // Check if cart drawer is already open on page load
    const cartDrawer = document.getElementById("CartDrawer");
    if (cartDrawer && cartDrawer.classList.contains("drawer--is-open")) {
      this.log("Cart drawer already open on page load");
      // Check if we have items in cart
      setTimeout(async () => {
        try {
          const cartItems = await this.getCartItems();
          if (cartItems.length > 0) {
            this.log(
              "Cart drawer open with items on page load, loading cross-sell products"
            );
            this.loadCrossSellProducts();

            // Also update shipping progress for initial state
            try {
              const cart = await this.fetchCartData();
              await this.updateShippingProgress(cart.total_price);
            } catch (error) {
              console.error(
                "Error updating shipping progress on initial load:",
                error
              );
            }
          }
        } catch (error) {
          console.error("Error checking cart items on page load:", error);
        }
      }, 200);
    }
  }

  setupDrawerClassObserver() {
    // Watch for cart drawer class changes to detect opening/closing
    const cartDrawer = document.getElementById("CartDrawer");
    if (!cartDrawer) return;

    const ObserverCtor =
      window["Mutation" + "Observer"] ||
      window["WebKit" + "Mutation" + "Observer"];
    const observer = ObserverCtor
      ? new ObserverCtor((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "class"
            ) {
              const isOpen = cartDrawer.classList.contains("drawer--is-open");
              if (isOpen) {
                this.log("Cart drawer opened via class change");
                // Setup focus trap
                this.setupFocusTrap();
                // Lock body scroll
                this.lockBodyScroll();
                setTimeout(async () => {
                  this.loadCrossSellProducts();

                  // Also update shipping progress when drawer opens
                  try {
                    const cart = await this.fetchCartData();
                    await this.updateShippingProgress(cart.total_price);
                  } catch (error) {
                    console.error(
                      "Error updating shipping progress on drawer open:",
                      error
                    );
                  }
                }, 100);
              } else {
                this.log("Cart drawer closed via class change");
                this.onDrawerClose();
              }
            }
          });
        })
      : null;

    if (observer) {
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  }

  async handleCartUpdate(cartData) {
    // Handle both AJAX add to cart and cart update events
    const itemCount = cartData.item_count;
    const totalPrice = cartData.total_price;

    this.log("Cart updated:", { itemCount, totalPrice, cartData });

    // Store previous cart state to detect empty to non-empty transition
    const wasEmpty = this.lastCartCount === 0;
    const previousCount = this.lastCartCount;
    this.lastCartCount = itemCount;

    this.updateCartCount(itemCount);
    await this.updateShippingProgress(totalPrice);

    if (window.GiftWithPurchase && typeof window.GiftWithPurchase.check === "function") {
      await window.GiftWithPurchase.check(cartData);
    }

    // Reset shown products if cart changed significantly (new items added/removed)
    if (Math.abs(itemCount - previousCount) > 0) {
      this.log(
        "Cart changed significantly, clearing shown products for fresh recommendations"
      );
      this.clearShownProducts();
    }

    // Load cross-sell products if cart went from empty to having items
    if (wasEmpty && itemCount > 0) {
      this.log(
        "Cart went from empty to having items, resetting cross-sell flag and loading products"
      );
      this.crossSellLoaded = false; // Reset the flag to allow loading
      setTimeout(() => {
        this.loadCrossSellProducts();
      }, 300); // Increased delay to ensure cart drawer is fully open
    }

    // Also check if we have items but cross-sell hasn't been loaded yet
    if (itemCount > 0 && !this.crossSellLoaded) {
      this.log("Cart has items but cross-sell not loaded yet, loading now");
      setTimeout(() => {
        this.loadCrossSellProducts();
      }, 300);
    }

    // If cart is empty, reset the flag so we can load when items are added
    if (itemCount === 0) {
      this.log("Cart is empty, resetting cross-sell flag for future loading");
      this.crossSellLoaded = false;
    }
  }

  async updateCartCount(count) {
    // If no count provided, fetch current cart data using cache
    if (count === undefined) {
      try {
        const cart = await this.fetchCartData();
        count = cart.item_count;
        return cart; // Return cart object for reuse
      } catch (error) {
        console.error("Error fetching cart count:", error);
        count = 0;
        return null;
      }
    }

    const cartCountElements = document.querySelectorAll("[data-cart-count]");
    this.log(
      "Updating cart count:",
      count,
      "Found elements:",
      cartCountElements.length
    );
    cartCountElements.forEach((element) => {
      element.textContent = count || 0;
    });

    const labelElements = document.querySelectorAll("[data-cart-count-label]");
    labelElements.forEach((label) => {
      const parent = label.closest("[data-cart-count-singular]") || label.parentElement;
      if (!parent) return;
      const singular =
        parent.dataset.cartCountSingular || "item";
      const plural =
        parent.dataset.cartCountPlural || "items";
      label.textContent = count === 1 ? singular : plural;
    });

    return null; // No cart data when count is provided
  }

  async updateShippingProgress(cartTotal) {
    this.log("=== UPDATE SHIPPING PROGRESS CALLED ===");
    this.log("Input cartTotal:", cartTotal);

    const freeShippingThreshold = this.getFreeShippingThreshold();
    const currency = this.getCurrentCurrency();
    const shouldShow = this.shouldShowFreeShippingBar();

    this.log("Shipping progress debug:", {
      cartTotal,
      freeShippingThreshold,
      currency,
      shouldShow,
      thresholdInPounds: freeShippingThreshold / 100,
    });

    const progressBar = document.querySelector(".shipping-progress__fill");
    const progressBarContainer = document.querySelector(
      ".shipping-progress__bar"
    );
    const progressText = document.querySelector(".shipping-remaining-amount");
    const progressContainer = document.querySelector(
      ".cart__shipping-progress"
    );
    const successContainer = document.querySelector(".cart__shipping-success");
    const shippingDetailsText = document.querySelector(
      "[data-shipping-details-text]"
    );

    this.log("Shipping progress elements found:", {
      progressBar: !!progressBar,
      progressBarContainer: !!progressBarContainer,
      progressText: !!progressText,
      progressContainer: !!progressContainer,
      successContainer: !!successContainer,
      shippingDetailsText: !!shippingDetailsText,
    });

    if (
      !progressBar ||
      !progressBarContainer ||
      !progressText ||
      !progressContainer ||
      !successContainer
    ) {
      this.log("Shipping progress elements not found:", {
        progressBar: !!progressBar,
        progressBarContainer: !!progressBarContainer,
        progressText: !!progressText,
        progressContainer: !!progressContainer,
        successContainer: !!successContainer,
      });
      return;
    }

    // Update shipping details text with dynamic threshold
    if (shippingDetailsText) {
      const currency = this.getCurrentCurrency();
      const currencySymbols = {
        GBP: "£",
        USD: "$",
        EUR: "€",
      };
      const symbol = currencySymbols[currency] || "£";
      const thresholdValue = (freeShippingThreshold / 100).toFixed(0);
      shippingDetailsText.textContent = `Free delivery on orders over ${symbol}${thresholdValue}`;
    }

    // Only show free shipping bar for GBP currency
    if (!this.shouldShowFreeShippingBar()) {
      progressContainer.style.display = "none";
      successContainer.style.display = "none";
      return;
    }

    // If cartTotal seems incorrect (0 or undefined), fetch fresh cart data using cache
    if (!cartTotal || cartTotal === 0) {
      this.log("Cart total seems incorrect, fetching fresh cart data...");
      try {
        const cart = await this.fetchCartData();
        cartTotal = cart.total_price;
        this.log("Fetched fresh cart total:", cartTotal);
      } catch (error) {
        console.error("Error fetching fresh cart data:", error);
        return;
      }
    }

    const percentage = Math.min((cartTotal / freeShippingThreshold) * 100, 100);
    const remaining = Math.max(freeShippingThreshold - cartTotal, 0);

    this.log("Updating shipping progress:", {
      cartTotal,
      remaining,
      percentage,
      currency: this.getCurrentCurrency(),
      threshold: freeShippingThreshold,
      shouldShow: true, // Always show when we have a valid cart total
    });

    if (remaining > 0) {
      // Show progress bar
      progressContainer.style.display = "block";
      successContainer.style.display = "none";

      // Update visual progress bar
      progressBar.style.width = `${percentage}%`;
      progressText.textContent = this.formatMoney(remaining);

      // Update ARIA values for screen readers
      const roundedPercentage = Math.round(percentage);
      progressBarContainer.setAttribute("aria-valuenow", roundedPercentage);

      // Update aria-label with current progress
      const currentLabel = `Free delivery progress: ${roundedPercentage}% complete`;
      progressBarContainer.setAttribute("aria-label", currentLabel);
    } else {
      // Show success message
      progressContainer.style.display = "none";
      successContainer.style.display = "flex";

      // Set progress to 100% for ARIA when free delivery is achieved
      progressBarContainer.setAttribute("aria-valuenow", "100");
      progressBarContainer.setAttribute(
        "aria-label",
        "Free delivery progress: 100% complete - You qualify for free delivery!"
      );
    }
  }

  async loadCrossSellProducts() {
    // Prevent multiple simultaneous loads - check this FIRST
    if (this.isLoadingCrossSell) {
      this.log("Cross-sell products already loading, skipping...");
      return;
    }

    // Check if we already loaded cross-sell products and cart has items, don't reload
    if (this.crossSellLoaded) {
      this.log(
        "Cross-sell products already loaded for non-empty cart, skipping..."
      );
      return;
    }

    // Set loading flag immediately to prevent concurrent calls
    this.isLoadingCrossSell = true;
    this.log("Starting cross-sell products load...");

    try {
      // Get the cross-sell container first
      const crossSellContainer = document.querySelector(
        "[data-cross-sell-products]"
      );
      if (!crossSellContainer) {
        this.log("Cross-sell container not found");
        return;
      }

      // Show skeleton loading immediately before any API calls
      this.showSkeletonLoading(crossSellContainer);

      // Check if we have items in cart
      const cartItems = await this.getCartItems();

      // If cart is empty, don't load cross-sell products
      if (cartItems.length === 0) {
        this.log("Cart is empty, hiding cross-sell section");
        crossSellContainer.parentElement.style.setProperty(
          "display",
          "none",
          "important"
        );
        return;
      }

      this.log("Loading cross-sell products...");
      let recommendations = [];
      let isPopularProducts = false;

      if (cartItems.length > 0) {
        // Get product recommendations based on cart items
        this.log("Getting product recommendations for cart items");
        recommendations = await this.getProductRecommendations(
          cartItems,
          false
        );
        this.currentRecommendations = recommendations; // Store current recommendations
        isPopularProducts = false;
      } else {
        // Don't show anything when cart is empty
        this.log("Cart is empty, hiding cross-sell section");
        recommendations = [];
        this.currentRecommendations = [];
        isPopularProducts = false;
      }

      this.log("Recommendations found:", recommendations);
      this.log("Recommendations length:", recommendations.length);

      // Force the cross-sell section to be visible if we have recommendations
      if (recommendations.length > 0) {
        this.log("Forcing cross-sell section to be visible");
        crossSellContainer.parentElement.style.setProperty(
          "display",
          "block",
          "important"
        );
        crossSellContainer.parentElement.style.setProperty(
          "visibility",
          "visible",
          "important"
        );
        crossSellContainer.parentElement.style.setProperty(
          "opacity",
          "1",
          "important"
        );

        this.log(
          "About to call renderCrossSellProducts with:",
          recommendations.length,
          "products"
        );
        this.renderCrossSellProducts(
          recommendations,
          crossSellContainer,
          isPopularProducts
        );
      } else {
        this.log("No recommendations, hiding cross-sell section");
        crossSellContainer.parentElement.style.setProperty(
          "display",
          "none",
          "important"
        );
      }

      // Mark as loaded
      this.crossSellLoaded = true;
    } catch (error) {
      console.error("Error loading cross-sell products:", error);
      // Hide the cross-sell section if there's an error
      const crossSellContainer = document.querySelector(
        "[data-cross-sell-products]"
      );
      if (crossSellContainer) {
        crossSellContainer.parentElement.style.display = "none";
      }
    } finally {
      // Reset loading flag
      this.isLoadingCrossSell = false;
    }
  }

  showSkeletonLoading(container) {
    // Show the container with skeleton loading immediately
    this.log("Showing cross-sell section with skeleton loading");
    container.parentElement.style.setProperty("display", "block", "important");
    container.parentElement.style.setProperty(
      "visibility",
      "visible",
      "important"
    );
    container.parentElement.style.setProperty("opacity", "1", "important");
    container.parentElement.classList.add("is-loading");

    // Show the refresh button when loading
    const refreshBtn = container.parentElement.querySelector(
      "[data-refresh-recommendations]"
    );
    if (refreshBtn) {
      refreshBtn.style.display = "flex";
    }

    // Add skeleton loading styles if not already added
    if (!document.querySelector("#cross-sell-loading-styles")) {
      const style = document.createElement("style");
      style.id = "cross-sell-loading-styles";
      style.textContent = `
        .cart__cross-sell.is-loading .cross-sell__products-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 1rem;
          margin-top: 1.5rem;
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        @media (min-width: 480px) {
          .cart__cross-sell.is-loading .cross-sell__products-list {
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
          }
        }
        .cross-sell-skeleton {
          background: #fff;
          border: 1px solid #e5e5e5;
          border-radius: 3px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          width: 100%;
          aspect-ratio: 1/1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: 0.75rem;
          list-style: none;
          position: relative;
          overflow: hidden;
        }
        .cross-sell-skeleton__image {
          width: 60%;
          aspect-ratio: 1/1;
          background: #f3f3f3;
          border-radius: 8px;
          margin-bottom: 12px;
          animation: skeleton-pulse 1.5s ease-in-out infinite;
          animation-delay: 0s;
        }
        .cross-sell-skeleton__select {
          height: 38px;
          width: 100%;
          background: #f3f3f3;
          border-radius: 3px;
          margin-bottom: 10px;
          animation: skeleton-pulse 1.5s ease-in-out infinite;
          animation-delay: 0.2s;
        }
        .cross-sell-skeleton__button {
          height: 36px;
          width: 100%;
          background: #f3f3f3;
          border-radius: 6px;
          animation: skeleton-pulse 1.5s ease-in-out infinite;
          animation-delay: 0.4s;
        }
        @keyframes skeleton-pulse {
          0%, 100% { 
            opacity: 1;
            background: #f3f3f3;
          }
          50% { 
            opacity: 0.7;
            background: #e8e8e8;
          }
        }
        
        @keyframes skeleton-shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .cross-sell-skeleton::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          animation: skeleton-shimmer 2s infinite;
          pointer-events: none;
        }
        
        /* Stagger animation for multiple skeletons */
        .cross-sell-skeleton:nth-child(1) .cross-sell-skeleton__image {
          animation-delay: 0s;
        }
        .cross-sell-skeleton:nth-child(1) .cross-sell-skeleton__select {
          animation-delay: 0.2s;
        }
        .cross-sell-skeleton:nth-child(1) .cross-sell-skeleton__button {
          animation-delay: 0.4s;
        }
        
        .cross-sell-skeleton:nth-child(2) .cross-sell-skeleton__image {
          animation-delay: 0.1s;
        }
        .cross-sell-skeleton:nth-child(2) .cross-sell-skeleton__select {
          animation-delay: 0.3s;
        }
        .cross-sell-skeleton:nth-child(2) .cross-sell-skeleton__button {
          animation-delay: 0.5s;
        }
        
        /* Regular cross-sell list styles */
        .cross-sell__products-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 1rem;
          margin-top: 1.5rem;
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        @media (min-width: 480px) {
          .cross-sell__products-list {
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
          }
        }
        
        .cross-sell-product {
          list-style: none;
        }
        
        /* Screen reader only class */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `;
      document.head.appendChild(style);
    }

    // Update the section title
    const titleElement =
      container.parentElement.querySelector(".cross-sell__title");
    if (titleElement) {
      titleElement.textContent = "Add these & save the trip";
    }

    // Show skeleton loading immediately
    const skeletonHTML = `
      <ul class="cross-sell__products-list" role="list" aria-label="Recommended products">
        <li class="cross-sell-skeleton" aria-hidden="true">
          <div class="cross-sell-skeleton__image" aria-hidden="true"></div>
          <div class="cross-sell-skeleton__select" aria-hidden="true"></div>
          <div class="cross-sell-skeleton__button" aria-hidden="true"></div>
        </li>
        <li class="cross-sell-skeleton" aria-hidden="true">
          <div class="cross-sell-skeleton__image" aria-hidden="true"></div>
          <div class="cross-sell-skeleton__select" aria-hidden="true"></div>
          <div class="cross-sell-skeleton__button" aria-hidden="true"></div>
        </li>
      </ul>
    `;

    container.innerHTML = skeletonHTML;
  }

  async getCartItems() {
    try {
      const cart = await this.fetchCartData();
      return cart.items || [];
    } catch (error) {
      console.error("Error fetching cart items:", error);
      return [];
    }
  }

  async getProductRecommendations(cartItems, isRefresh = false) {
    if (!cartItems.length) return [];

    try {
      // Get the first product from cart for recommendations
      const firstProduct = cartItems[0];
      const productId = firstProduct.product_id;

      this.log(
        "Getting recommendations for product ID:",
        productId,
        "isRefresh:",
        isRefresh
      );

      // Request more products if refreshing to ensure variety
      const limit = isRefresh ? 8 : 2;

      // Use Shopify's Product Recommendations API
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${limit}`
      );

      this.log("Recommendations API response status:", response.status);

      if (!response.ok) {
        throw new Error("Failed to fetch recommendations");
      }

      const data = await response.json();
      this.log("Recommendations API data:", data);

      // Filter out products already in cart
      const cartProductIds = cartItems.map((item) => item.product_id);
      let filteredProducts = data.products.filter(
        (product) => !cartProductIds.includes(product.id)
      );

      // If refreshing, filter out previously shown products
      if (isRefresh) {
        const previouslyShown = Array.from(this.shownProductIds);
        this.log("Previously shown products:", previouslyShown);

        filteredProducts = filteredProducts.filter(
          (product) => !previouslyShown.includes(product.id)
        );

        this.log("After filtering previously shown:", filteredProducts.length);

        // If we don't have enough new products, clear the shown products and try again
        if (filteredProducts.length < 2) {
          this.log(
            "Not enough new products, clearing shown products and trying again"
          );
          this.clearShownProducts();

          // Try one more time with cleared history
          const retryResponse = await fetch(
            `/recommendations/products.json?product_id=${productId}&limit=${limit}`
          );
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            filteredProducts = retryData.products.filter(
              (product) => !cartProductIds.includes(product.id)
            );
          }
        }
      }

      this.log("Final filtered recommendations:", filteredProducts);

      const result = filteredProducts.slice(0, 2).map((product) => ({
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.featured_image,
        url: `/products/${product.handle}`,
        available: product.available,
      }));

      // Add the shown products to our tracking
      if (result.length > 0) {
        this.addToShownProducts(result.map((p) => p.id));
      }

      return result;
    } catch (error) {
      console.error("Error fetching product recommendations:", error);
      return [];
    }
  }

  async getPopularProducts() {
    // Fallback to popular products
    try {
      const response = await fetch(
        "/collections/all/products.json?sort_by=best-selling&limit=2"
      );
      const data = await response.json();

      return data.products.map((product) => ({
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.featured_image,
        url: `/products/${product.handle}`,
        available: product.available,
      }));
    } catch (error) {
      console.error("Error fetching popular products:", error);
      return [];
    }
  }

  renderCrossSellProducts(products, container, isPopularProducts = false) {
    this.log("=== RENDER CROSS SELL PRODUCTS CALLED ===");
    this.log("Rendering cross-sell products:", products.length, "products");
    this.log("Products data:", products);
    this.log("Container:", container);
    this.log(
      "Container innerHTML before:",
      container.innerHTML.substring(0, 100) + "..."
    );

    if (!products || products.length === 0) {
      this.log("No products to render, hiding cross-sell section");
      container.parentElement.style.setProperty("display", "none", "important");
      return;
    }

    // Show the refresh button when we have products
    const refreshBtn = container.parentElement.querySelector(
      "[data-refresh-recommendations]"
    );
    if (refreshBtn) {
      refreshBtn.style.display = "flex";
    }

    const productsHTML = products
      .map(
        (product) => `
      <li class="cross-sell-product" data-product-handle="${
        product.url.split("/products/")[1]
      }">
        <a href="${
          product.url
        }" class="cross-sell-product__link" aria-label="View ${
          product.title
        } details">
          <div class="cross-sell-product__image">
            <img src="${product.image}" alt="${
          product.title
        }" loading="eager" data-cross-sell-image>
          </div>
          <div class="cross-sell-product__info">
            <h4 class="cross-sell-product__title">${product.title}</h4>
            <p class="cross-sell-product__price">${this.formatMoney(
              product.price
            )}</p>
          </div>
        </a>
        <div class="cross-sell-product__options">
          <label for="variant-${product.id}" class="sr-only">Select size for ${
          product.title
        }</label>
          <select 
            class="cross-sell-product__variant-select" 
            data-product-handle="${product.url.split("/products/")[1]}"
            id="variant-${product.id}"
            aria-label="Select size for ${product.title}"
          >
            <option value="">Loading sizes...</option>
          </select>
        </div>
        <button 
          type="button"
          class="btn btn--small cross-sell-product__add" 
          data-product-id="${product.id}"
          data-product-handle="${product.url.split("/products/")[1]}"
          aria-label="Add ${product.title} to cart"
          disabled
        >
          Add
        </button>
      </li>
    `
      )
      .join("");

    // Wrap products in a proper list structure
    const listHTML = `<ul class="cross-sell__products-list" role="list" aria-label="Recommended products">${productsHTML}</ul>`;

    this.log("Generated products HTML:", productsHTML);

    // Replace skeleton with real content immediately
    this.log("Replacing skeleton with real products immediately");
    container.innerHTML = listHTML;
    this.log(
      "Container innerHTML after replacement:",
      container.innerHTML.substring(0, 100) + "..."
    );

    // Wait for all images to load before removing skeleton
    this.waitForImagesToLoad(container)
      .then(() => {
        this.log("All images loaded, removing skeleton loading state");
        container.parentElement.classList.remove("is-loading");

        // Load variants for the new content
        return this.loadVariantOptions(container);
      })
      .then(() => {
        // Add event listeners to the new content (FINAL DOM!)
        this.addCrossSellEventListeners(container);

        // Animate in the real products
        const productElements = container.querySelectorAll(
          ".cross-sell-product"
        );
        productElements.forEach((product, index) => {
          setTimeout(() => {
            product.classList.add("loaded");
          }, index * 100); // Stagger the animations
        });

        // Update focus trap elements after cross-sell products are loaded
        setTimeout(() => {
          this.updateFocusTrapElements();
        }, 300);

        this.log(
          "Cross-sell section loading complete, skeleton replaced with real content"
        );
      });
  }

  async waitForImagesToLoad(container) {
    const images = container.querySelectorAll("[data-cross-sell-image]");
    this.log("Waiting for", images.length, "images to load");

    if (images.length === 0) {
      this.log("No images found, resolving immediately");
      return Promise.resolve();
    }

    const imagePromises = Array.from(images).map((img) => {
      return new Promise((resolve) => {
        // If image is already loaded, resolve immediately
        if (img.complete && img.naturalHeight !== 0) {
          this.log("Image already loaded:", img.src);
          resolve();
          return;
        }

        // If image failed to load, resolve anyway after a timeout
        const timeout = setTimeout(() => {
          this.log("Image load timeout for:", img.src);
          resolve();
        }, 5000); // 5 second timeout

        // Listen for load and error events
        img.addEventListener("load", () => {
          this.log("Image loaded successfully:", img.src);
          clearTimeout(timeout);
          resolve();
        });

        img.addEventListener("error", () => {
          this.log("Image failed to load:", img.src);
          clearTimeout(timeout);
          resolve(); // Resolve anyway to not block the process
        });
      });
    });

    return Promise.all(imagePromises);
  }

  async loadVariantOptions(container) {
    const variantSelects = container.querySelectorAll(
      ".cross-sell-product__variant-select"
    );
    this.log("Loading variants for", variantSelects.length, "products");
    const loadPromises = [];

    for (const select of variantSelects) {
      const productHandle = select.getAttribute("data-product-handle");
      this.log("Loading variants for product:", productHandle);

      const loadPromise = (async () => {
        try {
          // Fetch product details to get variants
          const response = await fetch(`/products/${productHandle}.js`);
          if (!response.ok) return;

          const product = await response.json();

          // Find the size option by checking option names, not values
          const sizeOptionIndex = this.findSizeOptionIndex(product);

          // Filter to show size variants if size option is found, otherwise show all available variants
          let variantsToShow;
          if (sizeOptionIndex !== -1) {
            // Show only variants that have a size selected (not "Default Title")
            variantsToShow = product.variants.filter((variant) => {
              if (!variant.available) return false;

              // Get the size value for this variant
              const sizeValue = variant[`option${sizeOptionIndex + 1}`];

              // Skip variants with "Default Title" or empty size values
              return (
                sizeValue &&
                sizeValue !== "Default Title" &&
                sizeValue.trim() !== ""
              );
            });
          } else {
            // No size option found, show all available variants
            variantsToShow = product.variants.filter((v) => v.available);
          }

          // If no variants found, show all available variants as fallback
          if (variantsToShow.length === 0) {
            variantsToShow = product.variants.filter((v) => v.available);
          }

          // Clear the loading option first
          select.innerHTML = "";

          // Add a default option first - use appropriate text based on whether we found a size option
          const defaultOption = document.createElement("option");
          defaultOption.value = "";
          defaultOption.textContent =
            sizeOptionIndex !== -1 ? "Choose size" : "Choose option";
          defaultOption.selected = true;
          select.appendChild(defaultOption);

          // Add variant options to select
          variantsToShow.forEach((variant) => {
            const option = document.createElement("option");
            option.value = variant.id;
            option.textContent = variant.title;
            option.disabled = !variant.available;
            select.appendChild(option);
          });

          // If no variants available, show a message
          if (variantsToShow.length === 0) {
            const option = document.createElement("option");
            option.value = "";
            option.textContent = "Out of stock";
            option.disabled = true;
            select.appendChild(option);
          }

          // Auto-select if there's only one available variant
          const availableVariants = variantsToShow.filter((v) => v.available);
          if (availableVariants.length === 1) {
            const singleVariant = availableVariants[0];
            select.value = singleVariant.id;

            // Enable the add button and set variant ID
            const productContainer = select.closest(".cross-sell-product");
            const addButton = productContainer.querySelector(
              ".cross-sell-product__add"
            );
            if (addButton) {
              addButton.disabled = false;
              addButton.setAttribute("data-variant-id", singleVariant.id);
            }

            this.log(
              "Auto-selected single variant:",
              singleVariant.title,
              "for product:",
              productHandle
            );
          } else {
            // Multiple variants or no variants - start with disabled add button
            const productContainer = select.closest(".cross-sell-product");
            const addButton = productContainer.querySelector(
              ".cross-sell-product__add"
            );
            if (addButton) {
              addButton.disabled = true;
              addButton.removeAttribute("data-variant-id");
            }
          }

          // Remove any previous error state
          const productContainer = select.closest(".cross-sell-product");
          if (productContainer) {
            const prevError = productContainer.querySelector(
              ".cross-sell-size-error"
            );
            if (prevError) prevError.remove();
            select.classList.remove("cross-sell-error");
          }
        } catch (error) {
          console.error(
            "Error loading variants for product:",
            productHandle,
            error
          );
        }
      })();

      loadPromises.push(loadPromise);
    }

    // Wait for all variant loading to complete
    await Promise.all(loadPromises);
    this.log("All variants loaded successfully");
  }

  findSizeOptionIndex(product) {
    // Check if product has options
    if (!product.options || product.options.length === 0) {
      return -1;
    }

    // Look for size-related option names
    const sizeKeywords = [
      "size",
      "sizes",
      "dimension",
      "dimensions",
      "measurement",
      "measurements",
    ];

    for (let i = 0; i < product.options.length; i++) {
      const optionName = product.options[i].name.toLowerCase();

      // Check if this option name contains any size-related keywords
      if (sizeKeywords.some((keyword) => optionName.includes(keyword))) {
        this.log(
          `Found size option at index ${i}: "${product.options[i].name}"`
        );
        return i;
      }
    }

    this.log("No size option found in product options");
    return -1;
  }

  addCrossSellEventListeners(container) {
    // Handle variant selection
    const variantSelects = container.querySelectorAll(
      ".cross-sell-product__variant-select"
    );
    variantSelects.forEach((select) => {
      select.addEventListener("change", (event) => {
        const productContainer = select.closest(".cross-sell-product");
        const addButton = productContainer.querySelector(
          ".cross-sell-product__add"
        );

        if (select.value) {
          addButton.disabled = false;
          addButton.setAttribute("data-variant-id", select.value);
          // Remove error state if present
          select.classList.remove("cross-sell-error");
          const prevError = productContainer.querySelector(
            ".cross-sell-size-error"
          );
          if (prevError) prevError.remove();
        } else {
          addButton.disabled = true;
          addButton.removeAttribute("data-variant-id");
        }
      });
      // Remove .shake class after animation ends so it can be retriggered
      select.addEventListener("animationend", () => {
        select.classList.remove("shake");
      });
    });

    // Handle add to cart
    const buttons = container.querySelectorAll(".cross-sell-product__add");
    buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (button.disabled) {
          // Show error feedback when trying to add without selecting size
          const productContainer = button.closest(".cross-sell-product");
          const select = productContainer.querySelector(
            ".cross-sell-product__variant-select"
          );

          // Add error state to select
          select.classList.add("cross-sell-error");

          // Add error message if not present
          if (!productContainer.querySelector(".cross-sell-size-error")) {
            const errorMsg = document.createElement("div");
            errorMsg.className = "cross-sell-size-error";
            errorMsg.textContent = "Please select a size";
            errorMsg.setAttribute("role", "alert");
            productContainer.appendChild(errorMsg);
          }

          // Trigger shake animation on the select
          select.classList.remove("shake");
          void select.offsetWidth; // reflow
          select.classList.add("shake");

          // Focus the dropdown for better UX
          select.focus();

          // Add visual feedback to the button itself
          button.classList.add("btn--error");
          setTimeout(() => {
            button.classList.remove("btn--error");
          }, 1000);

          return;
        }

        const variantId = button.getAttribute("data-variant-id");
        if (!variantId) {
          this.log("No variant selected");
          return;
        }

        this.addCrossSellProduct(
          button.getAttribute("data-product-handle"),
          variantId
        );
      });
    });
  }

  async addCrossSellProduct(productHandle, variantId) {
    this.log(
      "Adding product with handle:",
      productHandle,
      "variant:",
      variantId
    );

    try {
      // Add the selected variant to cart
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);

      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        this.log("Product added successfully:", result);

        // Invalidate cache since cart has changed
        this.invalidateCartCache();

        // Trigger cart update event
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", { detail: result })
        );

        // Show success feedback
        this.showAddToCartFeedback(productHandle);
      } else {
        const errorData = await response.json();
        console.error("Failed to add product:", errorData);
        this.showAddToCartError(productHandle);
      }
    } catch (error) {
      console.error("Error adding cross-sell product:", error);
      this.showAddToCartError(productHandle);
    }
  }

  showAddToCartFeedback(productHandle) {
    const productContainer = document.querySelector(
      `[data-product-handle="${productHandle}"]`
    );
    if (!productContainer) return;

    const button = productContainer.querySelector(".cross-sell-product__add");
    if (!button) return;

    // Get the product title for better accessibility
    const productTitle =
      productContainer.querySelector(".cross-sell-product__title")
        ?.textContent || "product";

    // Change button to show "Added to cart" state with clear text
    button.textContent = "Added to cart ✓";
    button.setAttribute("aria-label", `${productTitle} has been added to cart`);
    button.classList.add("btn--success");
    button.disabled = true;

    // Add a subtle visual indicator to the whole product
    productContainer.classList.add("added-to-cart");

    // Announce success to screen readers
    this.announceToScreenReader(`${productTitle} has been added to cart`);

    // Add CSS for the added state
    const style = document.createElement("style");
    style.textContent = `
      .cross-sell-product.added-to-cart {
        opacity: 0.7;
        position: relative;
      }
      .cross-sell-product.added-to-cart::after {
        content: '✓ Added to cart';
        position: absolute;
        top: 8px;
        right: 8px;
        background: #28a745;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        z-index: 1;
      }
      .cross-sell-product__add.btn--success {
        background-color: #28a745 !important;
        border-color: #28a745 !important;
        color: white !important;
      }
    `;
    if (!document.querySelector("#cross-sell-added-styles")) {
      style.id = "cross-sell-added-styles";
      document.head.appendChild(style);
    }
  }

  showAddToCartError(productHandle) {
    const productContainer = document.querySelector(
      `[data-product-handle="${productHandle}"]`
    );
    if (!productContainer) return;

    const button = productContainer.querySelector(".cross-sell-product__add");
    if (!button) return;

    // Get the product title for better accessibility
    const productTitle =
      productContainer.querySelector(".cross-sell-product__title")
        ?.textContent || "product";

    const originalText = button.textContent;
    const originalAriaLabel = button.getAttribute("aria-label");

    button.textContent = "Error adding to cart";
    button.setAttribute(
      "aria-label",
      `Error: Failed to add ${productTitle} to cart`
    );
    button.classList.add("btn--error");
    button.disabled = true;

    // Announce error to screen readers
    this.announceToScreenReader(`Error: Failed to add ${productTitle} to cart`);

    // Add error styles
    const style = document.createElement("style");
    style.textContent = `
      .cross-sell-product__add.btn--error {
        background-color: #dc3545 !important;
        border-color: #dc3545 !important;
        color: white !important;
      }
    `;
    if (!document.querySelector("#cross-sell-error-styles")) {
      style.id = "cross-sell-error-styles";
      document.head.appendChild(style);
    }

    // Reset after 3 seconds
    setTimeout(() => {
      button.textContent = originalText;
      if (originalAriaLabel) {
        button.setAttribute("aria-label", originalAriaLabel);
      } else {
        button.removeAttribute("aria-label");
      }
      button.classList.remove("btn--error");
      button.disabled = false;
    }, 3000);
  }

  async refreshCartData() {
    try {
      const cart = await this.fetchCartData();
      this.handleCartUpdate(cart);
    } catch (error) {
      console.error("Error refreshing cart data:", error);
    }
  }

  onDrawerOpen() {
    this.log(
      "Cart drawer opened - checking if cross-sell products need to be loaded"
    );

    // Setup focus trap
    this.setupFocusTrap();

    // Ensure body scrolling is locked for all devices
    this.lockBodyScroll();

    // Check if we have items in cart and load cross-sell products if needed
    setTimeout(async () => {
      try {
        const cartItems = await this.getCartItems();
        if (cartItems.length > 0 && !this.crossSellLoaded) {
          this.log("Drawer opened with items, loading cross-sell products");
          this.loadCrossSellProducts();
        } else if (cartItems.length > 0) {
          this.log("Drawer opened with items, but cross-sell already loaded");
        } else {
          this.log("Drawer opened with empty cart");
        }
        // Also refresh shipping progress to ensure it's correct
        await this.refreshCartData();

        // Force update shipping progress to ensure it's visible
        try {
          const cart = await this.fetchCartData();
          await this.updateShippingProgress(cart.total_price);
        } catch (error) {
          console.error(
            "Error updating shipping progress on drawer open:",
            error
          );
        }

        // Update focus trap elements after content loads
        setTimeout(() => {
          this.updateFocusTrapElements();
        }, 200);
      } catch (error) {
        console.error("Error checking cart items on drawer open:", error);
      }
    }, 100);
  }

  onDrawerClose() {
    this.log("Cart drawer closed - resetting cross-sell loaded flag");

    // Remove focus trap and return focus to previous element
    this.removeFocusTrap();

    // Unlock body scrolling
    this.unlockBodyScroll();

    this.crossSellLoaded = false;
  }

  formatMoney(cents) {
    // Use theme's currency formatting if available
    if (
      window.theme &&
      window.theme.Currency &&
      window.theme.Currency.formatMoney
    ) {
      return window.theme.Currency.formatMoney(cents);
    }

    // Fallback to GBP formatting
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(cents / 100);
  }

  getCurrentCurrency() {
    // Try multiple sources to detect current currency
    const shopifyActive = window.Shopify?.currency?.active;
    const shopifyRate = window.Shopify?.currency?.rate;
    const dataCurrency = document
      .querySelector("[data-currency]")
      ?.getAttribute("data-currency");
    const metaCurrency = document
      .querySelector('meta[property="og:price:currency"]')
      ?.getAttribute("content");

    const currency =
      shopifyActive || shopifyRate || dataCurrency || metaCurrency || "GBP";

    this.log("Currency detection:", {
      shopifyActive,
      shopifyRate,
      dataCurrency,
      metaCurrency,
      finalCurrency: currency,
    });

    return currency;
  }

  getFreeShippingThreshold() {
    // Get current currency
    const currency = this.getCurrentCurrency();

    // Get thresholds from theme settings (stored in data attributes)
    const cartForm = document.getElementById("CartDrawerForm");
    if (!cartForm) {
      // Fallback to hardcoded values if form not found
      const thresholds = {
        GBP: 5000, // £50.00
        USD: 5000, // $50.00
        EUR: 5000, // €50.00
      };
      return thresholds[currency] || 5000;
    }

    // Get thresholds from data attributes (convert to cents)
    const thresholds = {
      GBP:
        parseInt(cartForm.getAttribute("data-shipping-threshold-gbp") || "50") *
        100,
      USD:
        parseInt(cartForm.getAttribute("data-shipping-threshold-usd") || "50") *
        100,
      EUR:
        parseInt(cartForm.getAttribute("data-shipping-threshold-eur") || "50") *
        100,
    };

    return thresholds[currency] || thresholds["GBP"]; // Default to GBP if currency not found
  }

  shouldShowFreeShippingBar() {
    // Check if free shipping bar is enabled for the current currency
    const currency = this.getCurrentCurrency();
    const cartForm = document.getElementById("CartDrawerForm");

    this.log("=== SHOULD SHOW FREE SHIPPING BAR ===");
    this.log("Currency detected:", currency);
    this.log("Cart form found:", !!cartForm);

    if (!cartForm) {
      // Fallback: only show for GBP if form not found
      this.log(
        "No cart form found, fallback to GBP check:",
        currency === "GBP"
      );
      return currency === "GBP";
    }

    // Check the enable setting for the current currency
    const attributeName = `data-shipping-enable-${currency.toLowerCase()}`;
    const enableSetting = cartForm.getAttribute(attributeName);

    // Debug logging
    this.log("Free shipping bar check:", {
      currency,
      enableSetting,
      attributeName,
      shouldShow: enableSetting === "true",
      allFormAttributes: {
        "data-shipping-threshold-gbp": cartForm.getAttribute(
          "data-shipping-threshold-gbp"
        ),
        "data-shipping-enable-gbp": cartForm.getAttribute(
          "data-shipping-enable-gbp"
        ),
        "data-shipping-threshold-usd": cartForm.getAttribute(
          "data-shipping-threshold-usd"
        ),
        "data-shipping-enable-usd": cartForm.getAttribute(
          "data-shipping-enable-usd"
        ),
        "data-shipping-threshold-eur": cartForm.getAttribute(
          "data-shipping-threshold-eur"
        ),
        "data-shipping-enable-eur": cartForm.getAttribute(
          "data-shipping-enable-eur"
        ),
      },
    });

    // Check if the setting is enabled
    // The attribute will be 'true' or 'false' as a string
    return enableSetting === "true";
  }

  loadShownProductIds() {
    try {
      const stored = localStorage.getItem("cartCrossSellShownProducts");
      if (stored) {
        const data = JSON.parse(stored);
        // Only load if it's from today (reset daily)
        const today = new Date().toDateString();
        if (data.date === today) {
          this.shownProductIds = new Set(data.productIds);
          // Apply cap when loading to ensure consistency
          this.capShownProductIds();
          this.log("Loaded shown product IDs:", this.shownProductIds.size);
        } else {
          this.log("Clearing old shown product IDs (new day)");
          this.shownProductIds = new Set();
        }
      }
    } catch (error) {
      console.error("Error loading shown product IDs:", error);
      this.shownProductIds = new Set();
    }
  }

  saveShownProductIds() {
    try {
      // Cap the history to prevent unbounded growth
      const cappedProductIds = this.capShownProductIds();

      const data = {
        date: new Date().toDateString(),
        productIds: cappedProductIds,
      };
      localStorage.setItem("cartCrossSellShownProducts", JSON.stringify(data));
      this.log(
        "Saved shown product IDs:",
        cappedProductIds.length,
        "(capped at",
        this.MAX_SHOWN_PRODUCT_IDS,
        ")"
      );
    } catch (error) {
      console.error("Error saving shown product IDs:", error);
    }
  }

  addToShownProducts(productIds) {
    productIds.forEach((id) => this.shownProductIds.add(id));
    this.saveShownProductIds();
  }

  capShownProductIds() {
    // Convert Set to Array and keep only the most recent IDs
    // Strategy: Keep the last N IDs (most recent) to maintain relevance
    // This prevents localStorage payload from growing unbounded
    const productIdsArray = Array.from(this.shownProductIds);

    if (productIdsArray.length <= this.MAX_SHOWN_PRODUCT_IDS) {
      // No capping needed
      return productIdsArray;
    }

    // Keep only the last MAX_SHOWN_PRODUCT_IDS items (most recent)
    const cappedIds = productIdsArray.slice(-this.MAX_SHOWN_PRODUCT_IDS);

    // Update the Set to match the capped array
    this.shownProductIds = new Set(cappedIds);

    this.log(
      "Capped shown product IDs from",
      productIdsArray.length,
      "to",
      cappedIds.length
    );

    return cappedIds;
  }

  getShownProductIdsSize() {
    return this.shownProductIds.size;
  }

  getLocalStorageStats() {
    try {
      const stored = localStorage.getItem("cartCrossSellShownProducts");
      if (stored) {
        const data = JSON.parse(stored);
        const sizeInBytes = new Blob([stored]).size;
        return {
          hasData: true,
          productCount: data.productIds ? data.productIds.length : 0,
          date: data.date,
          sizeInBytes,
          sizeInKB: (sizeInBytes / 1024).toFixed(2),
          isCapped:
            data.productIds &&
            data.productIds.length >= this.MAX_SHOWN_PRODUCT_IDS,
        };
      }
      return { hasData: false };
    } catch (error) {
      return { hasData: false, error: error.message };
    }
  }

  clearShownProducts() {
    this.shownProductIds.clear();
    localStorage.removeItem("cartCrossSellShownProducts");
    this.log("Cleared shown product IDs");
  }

  showNoMoreRecommendationsMessage() {
    const refreshBtn = document.querySelector("[data-refresh-recommendations]");
    if (!refreshBtn) return;

    const originalTitle = refreshBtn.getAttribute("title");
    const originalAriaLabel = refreshBtn.getAttribute("aria-label");

    // Temporarily change the button to show "No more recommendations"
    refreshBtn.setAttribute("title", "No more recommendations available");
    refreshBtn.setAttribute("aria-label", "No more recommendations available");
    refreshBtn.style.opacity = "0.5";
    refreshBtn.style.cursor = "not-allowed";

    // Reset after 3 seconds
    setTimeout(() => {
      refreshBtn.setAttribute("title", originalTitle);
      refreshBtn.setAttribute("aria-label", originalAriaLabel);
      refreshBtn.style.opacity = "";
      refreshBtn.style.cursor = "";
    }, 3000);
  }

  async refreshRecommendations() {
    const refreshBtn = document.querySelector("[data-refresh-recommendations]");
    if (!refreshBtn || this.isLoadingCrossSell) {
      this.log("Refresh button not found or already loading");
      return;
    }

    // Show loading state
    refreshBtn.classList.add("is-loading");
    refreshBtn.disabled = true;

    try {
      // Get current cart items
      const cartItems = await this.getCartItems();
      if (cartItems.length === 0) {
        this.log("Cart is empty, cannot refresh recommendations");
        return;
      }

      // Fetch new recommendations
      const newRecommendations = await this.getProductRecommendations(
        cartItems,
        true
      );

      if (newRecommendations.length > 0) {
        // Update current recommendations
        this.currentRecommendations = newRecommendations;

        // Re-render with new products
        const crossSellContainer = document.querySelector(
          "[data-cross-sell-products]"
        );
        if (crossSellContainer) {
          this.renderCrossSellProducts(
            newRecommendations,
            crossSellContainer,
            false
          );
        }

        this.log("Recommendations refreshed successfully");
      } else {
        this.log("No new recommendations available");
        // Show a brief message that we've shown all available recommendations
        this.showNoMoreRecommendationsMessage();
      }
    } catch (error) {
      console.error("Error refreshing recommendations:", error);
    } finally {
      // Remove loading state
      refreshBtn.classList.remove("is-loading");
      refreshBtn.disabled = false;
    }
  }

  announceToScreenReader(message) {
    // Create or get existing live region for announcements
    let liveRegion = document.getElementById("cart-announcements");
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "cart-announcements";
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      document.body.appendChild(liveRegion);
    }

    // Announce the message
    liveRegion.textContent = message;

    // Clear the message after a short delay to allow for future announcements
    setTimeout(() => {
      liveRegion.textContent = "";
    }, 1000);
  }

  // Focus trap methods
  setupFocusTrap() {
    const cartDrawer = document.getElementById("CartDrawer");
    if (!cartDrawer) return;

    // Store the element that had focus before the drawer opened
    this.previousActiveElement = document.activeElement;

    // Add ARIA attributes for accessibility
    cartDrawer.setAttribute("aria-modal", "true");
    cartDrawer.setAttribute("role", "dialog");
    cartDrawer.setAttribute("aria-label", "Shopping cart");

    // Get all focusable elements in the drawer
    this.focusTrapElements = this.getFocusableElements(cartDrawer);

    // Set initial focus to the close button
    const closeButton = cartDrawer.querySelector(".js-drawer-close");
    if (closeButton) {
      closeButton.focus();
    }

    // Add event listener for keyboard navigation (store the bound handler once)
    if (!this._boundFocusKeydown) {
      this._boundFocusKeydown = this.handleFocusTrapKeydown.bind(this);
    }
    cartDrawer.addEventListener("keydown", this._boundFocusKeydown);
  }

  getFocusableElements(container) {
    const focusableSelectors = [
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "a[href]",
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ];

    const elements = container.querySelectorAll(focusableSelectors.join(", "));
    return Array.from(elements).filter((element) => {
      // Filter out hidden elements
      const style = window.getComputedStyle(element);
      return (
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        style.opacity !== "0"
      );
    });
  }

  handleFocusTrapKeydown(event) {
    if (event.key === "Escape") {
      // Close drawer on Escape key
      const closeButton = document.querySelector(".js-drawer-close");
      if (closeButton) {
        closeButton.click();
      }
      return;
    }

    if (event.key !== "Tab") return;

    const firstElement = this.focusTrapElements[0];
    const lastElement =
      this.focusTrapElements[this.focusTrapElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab: going backwards
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: going forwards
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  removeFocusTrap() {
    const cartDrawer = document.getElementById("CartDrawer");
    if (!cartDrawer) return;

    // Remove the keyboard event listener (use the stored bound handler)
    if (this._boundFocusKeydown) {
      cartDrawer.removeEventListener("keydown", this._boundFocusKeydown);
      this._boundFocusKeydown = null; // Clear the reference to prevent memory leak
    }

    // Remove ARIA attributes
    cartDrawer.removeAttribute("aria-modal");
    cartDrawer.removeAttribute("role");
    cartDrawer.removeAttribute("aria-label");

    // Return focus to the previous element
    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus();
    }

    // Clear stored elements
    this.previousActiveElement = null;
    this.focusTrapElements = [];
  }

  updateFocusTrapElements() {
    const cartDrawer = document.getElementById("CartDrawer");
    if (!cartDrawer) return;

    // Update the list of focusable elements (useful when content changes dynamically)
    this.focusTrapElements = this.getFocusableElements(cartDrawer);
  }

  lockBodyScroll() {
    // Store current scroll position
    this.scrollPosition = window.pageYOffset;

    // Apply multiple strategies to prevent scrolling across all devices
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = "100%";

    // Add touch-action: none to prevent iOS momentum scrolling
    document.body.style.touchAction = "none";

    this.log("Body scroll locked, stored position:", this.scrollPosition);
  }

  unlockBodyScroll() {
    // Restore scroll position and remove lock styles
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    document.body.style.touchAction = "";

    // Restore scroll position
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
      this.scrollPosition = undefined;
    }

    this.log("Body scroll unlocked");
  }

  // Cleanup method to ensure scrolling is unlocked
  cleanup() {
    this.log("Cleaning up cart drawer enhancements");
    this.unlockBodyScroll();
    this.removeFocusTrap();
    this.cleanupUrlChangeListener();
    this.cleanupViewportHeightFix();
  }
}

// Initialize cart drawer enhancements (singleton pattern prevents multiple instances)
const cartDrawerEnhancements = new CartDrawerEnhancements();

// Export for global access
window.cartDrawerEnhancements = cartDrawerEnhancements;

// Expose cache stats for debugging (only in debug mode)
if (cartDrawerEnhancements.DEBUG) {
  window.getCartCacheStats = () => cartDrawerEnhancements.getCacheStats();
  window.forceRefreshCartData = () =>
    cartDrawerEnhancements.forceRefreshCartData();
  window.invalidateCartCache = () =>
    cartDrawerEnhancements.invalidateCartCache();
  window.resetCacheStats = () => {
    cartDrawerEnhancements.cacheHitCount = 0;
    cartDrawerEnhancements.cacheMissCount = 0;
    cartDrawerEnhancements.networkRequestCount = 0;
    cartDrawerEnhancements.manualRefreshCount = 0;
    console.log("Cache stats reset");
  };
}
